import { Observable, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { intervalToDuration, daysToWeeks, differenceInSeconds, isValid, parseISO } from 'date-fns';

import { Pipe, PipeTransform } from '@angular/core';

import { PhxTranslatePipe } from '../../translation/phx-translation.pipe';

import { PhxDateFnsFormatter } from '../../services/phx-date-fns-formatter-service';

import { upperFirst } from '../../utils';

const INTERVAL: number = 1 * 60000; // 1 min
const DATE_TIME_TINY_FORMAT: string = 'M/d/yyyy';
const DATE_TIME_FORMAT: string = 'MMMM d, yyyy';

type PhxTimeUnitsType = 'week' | 'day' | 'hour' | 'minute' | 'second';

type PhxRelativeTimeFormatType = 'long' | 'compact' | 'tiny';

type PhxTimeUnitLabelsType = Record<PhxRelativeTimeFormatType, Record<PhxTimeUnitsType, string>>;

@Pipe({ name: 'phxRelativeTime' })
export class PhxRelativeTimePipe implements PipeTransform {
  // Moving these constant inside pipe as they cannot be translated without using a pipe function
  private readonly TIME_DURATION_STATIC_LABELS: { [key: string]: string } = {
    JUST_NOW: this._phxTranslate.transform('just now'),
    NOW: this._phxTranslate.transform('now'),
    AGO: this._phxTranslate.transform('ago'),
    YESTERDAY_AT: this._phxTranslate.transform('yesterday at'),

    // Time Unit - Long Format - Plural Forms
    PLURAL_WEEK: this._phxTranslate.transform('weeks'),
    PLURAL_DAY: this._phxTranslate.transform('days'),
    PLURAL_HOUR: this._phxTranslate.transform('hours'),
    PLURAL_MINUTE: this._phxTranslate.transform('minutes'),
    PLURAL_SECOND: this._phxTranslate.transform('seconds'),
  };
  private readonly TIME_UNIT_LABELS: PhxTimeUnitLabelsType = {
    // Adding spaces here so we can avoid duplicate code while formatting
    long: {
      week: ' ' + this._phxTranslate.transform('week'),
      day: ' ' + this._phxTranslate.transform('day'),
      hour: ' ' + this._phxTranslate.transform('hour'),
      minute: ' ' + this._phxTranslate.transform('minute'),
      second: ' ' + this._phxTranslate.transform('second'),
    },
    compact: {
      week: ' ' + this._phxTranslate.transform('wk'),
      day: ' ' + this._phxTranslate.transform('day'),
      hour: ' ' + this._phxTranslate.transform('hr'),
      minute: ' ' + this._phxTranslate.transform('min'),
      second: ' ' + this._phxTranslate.transform('sec'),
    },
    // Not sure if we need to translate these, have to confirm
    tiny: {
      week: 'w',
      day: 'd',
      hour: 'h',
      minute: 'm',
      second: 's',
    },
  };

  constructor(
    private dateFnsFormatter: PhxDateFnsFormatter,
    private _phxTranslate: PhxTranslatePipe,
  ) { }

  public transform(
    updatedAt: string,
    format: PhxRelativeTimeFormatType,
    timezone: string,
    showRoundFormat: boolean = true,
    capitalize: boolean = true,
  ): Observable<string> {
    if (!updatedAt) return;
    return timer(0, INTERVAL).pipe(
      map((): string => {
        const result: string = this.format(updatedAt, format, timezone, showRoundFormat);
        return capitalize ? upperFirst(result) : result;
      }),
    );
  }

  private format(
    updatedAt: Date | string | number,
    format: PhxRelativeTimeFormatType,
    timezone: string,
    showRoundFormat: boolean,
  ): string {
    const thresholdSecsForNow: number = 0;
    const now: Date = new Date();
    const updatedAtDate: Date = typeof (updatedAt) === 'string' ? parseISO(updatedAt) : new Date(updatedAt);

    if (!isValid(updatedAtDate)) return '';

    // -1 : updatedAtDate is before now
    // 1: : updatedAtDate is after now
    // 0 : updatedAtDate is equal to now
    const timeDurationInSeconds: number = differenceInSeconds(updatedAtDate, now);
    const timeDuration: Duration = intervalToDuration({ start: now, end: updatedAtDate });

    let formattedTimeDuration: string;
    if (Math.abs(timeDurationInSeconds) <= thresholdSecsForNow) {
      switch (format) {
        case 'long':
          formattedTimeDuration = this.TIME_DURATION_STATIC_LABELS.JUST_NOW;
          break;
        case 'compact':
        case 'tiny':
          formattedTimeDuration = this.TIME_DURATION_STATIC_LABELS.NOW;
          break;
      }
    } else if (timeDurationInSeconds < 0) {
      formattedTimeDuration = this.formatTimeDurationInPast(
        updatedAtDate,
        timeDuration,
        format,
        timezone,
        showRoundFormat);
    } else if (timeDurationInSeconds > 0) {
      formattedTimeDuration = this.formatTimeDurationInFuture(
        updatedAtDate,
        timeDuration,
        format,
        timezone,
        showRoundFormat);
    }
    return formattedTimeDuration;
  }

  /**
   * Formats the date using Motive UI guideline - Relative Dates (https://docs.google.com/document/d/1BcyQcv4J0f3Z6bXcjyNWkYWr9x09SkgatKkUo6XOFKQ/edit?usp=sharing)
   * @param {Date} updatedAtDate - Date for which the relative time needs to be calculated
   * @param {Duration} timeDuration - Time duration between now and updatedAtDate
   * @param {PhxRelativeTimeFormatType} format - Format of the relative time
   * @param {string} timezone - Timezone to be used for formatting
   * @param {boolean} showRoundFormat - Whether to show round format or not
   * @returns returns formatted string
   */
  private formatTimeDurationInPast(
    updatedAtDate: Date,
    timeDuration: Duration,
    format: PhxRelativeTimeFormatType,
    timezone: string,
    showRoundFormat: boolean,
  ): string {
    const postFixString: string = this.TIME_DURATION_STATIC_LABELS.AGO;
    const timeDiffInYears: number = timeDuration?.years;
    const timeDiffInMonths: number = timeDuration?.months;
    const timeDiffInDays = timeDuration?.days;
    const timeDiffInWeeks: number = timeDiffInDays > 0 ? daysToWeeks(timeDiffInDays) : 0;
    const remainingDaysInAMonth: number = timeDiffInWeeks > 0 ? timeDiffInDays - (timeDiffInWeeks * 7) : 0;
    const timeDiffInHours: number = timeDuration?.hours;
    const timeDiffInMinutes: number = timeDuration?.minutes;
    const timeDiffInSeconds: number = timeDuration?.seconds;

    let updatedAtLabel: string;
    if (timeDiffInYears >= 1 || timeDiffInMonths >= 1 || timeDiffInDays >= 29) {
      switch (format) {
        case 'long':
        case 'compact':
          updatedAtLabel = this.dateFnsFormatter.format(updatedAtDate, DATE_TIME_FORMAT, timezone);
          break;
        case 'tiny':
          updatedAtLabel = this.dateFnsFormatter.format(updatedAtDate, DATE_TIME_TINY_FORMAT, timezone);
          break;
      }
    } else if (timeDiffInDays >= 1 && timeDiffInDays < 2) {
      switch (format) {
        case 'long':
          const prefixTextLongOneDay = this.TIME_DURATION_STATIC_LABELS.YESTERDAY_AT;
          updatedAtLabel = `${prefixTextLongOneDay} ${this.dateFnsFormatter.format(updatedAtDate, 'h:mm a zzz', timezone)}`;
          break;
        case 'compact':
        case 'tiny':
          const formattedDaysAndHours: string = timeDiffInHours > 0 ?
            `${timeDiffInDays}${this.pluralizeBasedOnValue(timeDiffInDays, 'day', format)} ${timeDiffInHours}${this.pluralizeBasedOnValue(timeDiffInHours, 'hour', format)}` :
            `${timeDiffInDays}${this.TIME_UNIT_LABELS[format].day}`;
          updatedAtLabel = `${formattedDaysAndHours} ${postFixString}`;
          break;
      }
    } else if (timeDiffInDays >= 2 && timeDiffInDays < 8) {
      const formattedDaysAndHours: string = timeDiffInHours > 0 ?
        `${timeDiffInDays}${this.pluralizeBasedOnValue(timeDiffInDays, 'day', format)} ${timeDiffInHours}${this.pluralizeBasedOnValue(timeDiffInHours, 'hour', format)}` :
        `${timeDiffInDays}${this.pluralizeBasedOnValue(timeDiffInDays, 'day', format)}`
      ;
      updatedAtLabel = `${formattedDaysAndHours} ${postFixString}`;
    } else if (timeDiffInDays >= 8 && timeDiffInDays <= 28) {
      if (showRoundFormat) {
        updatedAtLabel = `${timeDiffInDays}${this.pluralizeBasedOnValue(timeDiffInDays, 'day', format)} ${postFixString}`;
      } else {
        const formattedWeeksAndDays: string = remainingDaysInAMonth > 0 ?
          `${timeDiffInWeeks}${this.pluralizeBasedOnValue(timeDiffInWeeks, 'week', format)} ${remainingDaysInAMonth}${this.pluralizeBasedOnValue(timeDiffInDays, 'day', format)}` :
          `${timeDiffInWeeks}${this.pluralizeBasedOnValue(timeDiffInWeeks, 'week', format)}`
        ;
        updatedAtLabel = `${formattedWeeksAndDays} ${postFixString}`;
      }
    } else if (timeDiffInHours >= 1) {
      const formattedHoursAndMinutes: string = timeDiffInMinutes > 0 ?
        `${timeDiffInHours}${this.pluralizeBasedOnValue(timeDiffInHours, 'hour', format)} ${timeDiffInMinutes}${this.pluralizeBasedOnValue(timeDiffInMinutes, 'minute', format)}` :
        `${timeDiffInHours}${this.pluralizeBasedOnValue(timeDiffInHours, 'hour', format)}`
      ;
      updatedAtLabel = `${formattedHoursAndMinutes} ${postFixString}`;
    } else if (timeDiffInMinutes >= 1) {
      const formattedMinutesAndSeconds: string = timeDiffInSeconds > 0 ?
        `${timeDiffInMinutes}${this.pluralizeBasedOnValue(timeDiffInMinutes, 'minute', format)} ${timeDiffInSeconds}${this.pluralizeBasedOnValue(timeDiffInSeconds, 'second', format)}` :
        `${timeDiffInMinutes}${this.pluralizeBasedOnValue(timeDiffInMinutes, 'minute', format)}`
      ;
      updatedAtLabel = `${formattedMinutesAndSeconds} ${postFixString}`;
    } else if (timeDiffInSeconds >= 1) {
      updatedAtLabel = `${timeDiffInSeconds}${this.pluralizeBasedOnValue(timeDiffInSeconds, 'second', format)} ${postFixString}`;
    }
    return updatedAtLabel;
  }

  private formatTimeDurationInFuture(
    updatedAtDate: Date,
    timeDuration: Duration,
    format: PhxRelativeTimeFormatType,
    timezone: string,
    showRoundFormat: boolean,
  ): string {
    // TODO: need to handle future time duration
    throw new Error('Not implemented');
  }

  private pluralizeBasedOnValue(value: number, unit: PhxTimeUnitsType, format: PhxRelativeTimeFormatType): string {
    // Only need plural value for long format and for `day` in compact format
    if ((format === 'compact' && unit !== 'day') || format === 'tiny') return this.TIME_UNIT_LABELS[format][unit];
    return value > 1 ?
      ' ' + this.TIME_DURATION_STATIC_LABELS[`PLURAL_${unit.toUpperCase()}`] :
      this.TIME_UNIT_LABELS[format][unit]
    ;
  }
}
